/** Shared retailer directory (stale-while-revalidate).
 *
 * One module-level copy of the persisted retailer catalog shared by every
 * consumer, so the picker never refetches on open and remounts render the last
 * known list instantly. Revalidation sends the held `version`; an unchanged
 * directory comes back without items. Directory reads are Ezisell DB reads
 * only — never a retailer or provider call.
 */
import { useEffect, useSyncExternalStore } from "react";
import { listSourcingSources } from "./api";

export const DIRECTORY_REFRESH_MS = 30_000;
const STORAGE_KEY = "ezisell.retailerDirectory.v1";
const STORAGE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const EMPTY = {
  items: [],
  version: null,
  status: "idle", // idle | loading | ready | error
  refreshFailed: false,
  fetchedAt: 0,
  newlyReady: [],
};

let state = EMPTY;
let inflight = null;
let hydrated = false;
const listeners = new Set();

function setState(patch) {
  state = { ...state, ...patch };
  listeners.forEach((fn) => fn());
}

const selectable = (s) => Boolean(s?.enabled && s?.operational);

function hydrate() {
  if (hydrated) return;
  hydrated = true;
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw);
    if (!Array.isArray(saved?.items) || Date.now() - (saved.savedAt || 0) > STORAGE_MAX_AGE_MS) return;
    // Shown only until the immediate revalidation lands. Assigned without
    // notifying: hydrate runs before the first snapshot read.
    state = { ...state, items: saved.items, version: saved.version || null, status: "ready", fetchedAt: 0 };
  } catch {
    // Unreadable cache is ignored; the network copy wins.
  }
}

function persist(items, version) {
  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify({ items, version, savedAt: Date.now() }));
  } catch {
    // Storage full/blocked: in-memory cache still works.
  }
}

export function refreshRetailerDirectory() {
  hydrate();
  if (inflight) return inflight;
  const hadItems = state.items.length > 0;
  if (!hadItems) setState({ status: "loading" });
  inflight = listSourcingSources({ knownVersion: hadItems ? state.version : undefined })
    .then((data) => {
      if (data?.unchanged) {
        setState({ status: "ready", refreshFailed: false, fetchedAt: Date.now() });
        return;
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      const before = new Map(state.items.map((s) => [s.source_id, selectable(s)]));
      const newlyReady = hadItems
        ? items.filter((s) => selectable(s) && before.has(s.source_id) && !before.get(s.source_id)).map((s) => s.source_id)
        : [];
      setState({
        items,
        version: data?.version ?? null,
        status: "ready",
        refreshFailed: false,
        fetchedAt: Date.now(),
        newlyReady: newlyReady.length ? newlyReady : state.newlyReady,
      });
      persist(items, data?.version ?? null);
    })
    .catch(() => {
      // Keep a usable list visible; only a cold failure is an error state.
      if (state.items.length) setState({ refreshFailed: true, status: "ready" });
      else setState({ status: "error" });
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Start loading as early as possible (page mount); safe to call repeatedly. */
export const preloadRetailerDirectory = refreshRetailerDirectory;

export function clearNewlyReady() {
  if (state.newlyReady.length) setState({ newlyReady: [] });
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const getSnapshot = () => state;

export function useRetailerDirectory({ refreshMs = DIRECTORY_REFRESH_MS } = {}) {
  hydrate();
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    void refreshRetailerDirectory();
    const revalidate = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - state.fetchedAt >= refreshMs) void refreshRetailerDirectory();
    };
    const timer = setInterval(revalidate, refreshMs);
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", revalidate);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", revalidate);
    };
  }, [refreshMs]);
  return snap;
}

/** Test hook: forget all cached state. */
export function resetRetailerDirectory() {
  state = EMPTY;
  inflight = null;
  hydrated = false;
  try {
    window.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  listeners.forEach((fn) => fn());
}
